import { supabase } from '../supabaseClient';

const APP_EVENT_QUEUE_KEY = 'ss_event_queue';
const INTEREST_EVENT_QUEUE_KEY = 'ss_interest_event_queue';
const MAX_CONTEXT_BYTES = 12000;
const INTEREST_EVENT_TYPES = {
  da_open: 'viewed_da',
  da_show_on_map: 'viewed_da_on_map',
  da_track: 'tracked_da',
  da_untrack: 'untracked_da',
  search_performed: 'search',
  watch_zone_create: 'watched_area',
  watch_zone_radius_update: 'watched_area_updated',
  watch_zone_delete: 'watched_area_removed',
};

function getSessionId() {
  try {
    let id = sessionStorage.getItem('ss_session_key');
    if (!id && crypto?.randomUUID) {
      id = crypto.randomUUID();
      sessionStorage.setItem('ss_session_key', id);
    }
    return id || null;
  } catch {
    return null;
  }
}

function safeContext(context = {}) {
  try {
    const json = JSON.stringify(context);
    if (json.length <= MAX_CONTEXT_BYTES) return context;
    return {
      truncated: true,
      original_bytes: json.length,
    };
  } catch {
    return { unserialisable: true };
  }
}

function queueEvent(key, payload) {
  try {
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.push(payload);
    localStorage.setItem(key, JSON.stringify(existing.slice(-100)));
  } catch {}
}

async function flushQueue(key, table) {
  if (!navigator.onLine) return;
  let queued = [];
  try {
    queued = JSON.parse(localStorage.getItem(key) || '[]');
  } catch {
    queued = [];
  }
  if (!queued.length) return;

  const { error } = await supabase.from(table).insert(queued);
  if (!error) {
    try { localStorage.removeItem(key); } catch {}
  }
}

export async function flushQueuedEvents() {
  await flushQueue(APP_EVENT_QUEUE_KEY, 'app_events');
  await flushQueue(INTEREST_EVENT_QUEUE_KEY, 'user_interest_events');
}

function costBand(value) {
  if (value === null || value === undefined || value === '') return null;
  const amount = typeof value === 'number'
    ? value
    : Number(String(value).replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(amount) || amount < 0) return null;
  if (amount < 250000) return 'under_250k';
  if (amount < 500000) return '250k_500k';
  if (amount < 1000000) return '500k_1m';
  if (amount < 2000000) return '1m_2m';
  if (amount < 5000000) return '2m_5m';
  return '5m_plus';
}

function compactText(value) {
  if (Array.isArray(value)) return value.filter(Boolean).slice(0, 8).join(', ');
  if (value === null || value === undefined) return null;
  return String(value).slice(0, 240);
}

export function buildUserInterestEvent(eventName, appPayload, context = {}, options = {}) {
  const eventType = INTEREST_EVENT_TYPES[eventName];
  if (!eventType) return null;

  return {
    user_id:             appPayload.user_id,
    event_type:          eventType,
    da_id:               appPayload.da_id,
    council:             compactText(appPayload.council || context.council || context.councils),
    category:            compactText(appPayload.category || context.category || context.categories),
    estimated_cost_band: costBand(appPayload.estimated_cost || context.estimated_cost),
    suburb:              compactText(appPayload.suburb || context.suburb),
    timestamp:           appPayload.occurred_at,
    source:              compactText(options.source || context.source || appPayload.source),
  };
}

export async function trackAppEvent(eventName, context = {}, options = {}) {
  if (!eventName) return;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) return;

    const payload = {
      user_id:        user.id,
      session_id:     getSessionId(),
      event_name:     eventName,
      page:           options.page || null,
      source:         options.source || null,
      da_id:          options.da?.id || options.da_id || null,
      tracked_da_id:  options.tracked_da_id || null,
      council:        options.da?.council || options.council || null,
      da_number:      options.da?.da_number || options.da?.council_application_number || options.da_number || null,
      category:       options.da?.category || options.category || null,
      status:         options.da?.status || options.status || null,
      suburb:         options.da?.suburb || options.suburb || null,
      estimated_cost: options.da?.estimated_cost || options.da?.cost_of_development || options.estimated_cost || null,
      context:        safeContext(context),
      occurred_at:    new Date().toISOString(),
    };

    const interestPayload = buildUserInterestEvent(eventName, payload, context, options);

    if (!navigator.onLine) {
      queueEvent(APP_EVENT_QUEUE_KEY, payload);
      if (interestPayload) queueEvent(INTEREST_EVENT_QUEUE_KEY, interestPayload);
      return;
    }

    await flushQueuedEvents();
    const { error } = await supabase.from('app_events').insert(payload);
    if (error) {
      console.warn('[analytics] event insert failed:', error.message);
      queueEvent(APP_EVENT_QUEUE_KEY, payload);
    }

    if (interestPayload) {
      const { error: interestError } = await supabase.from('user_interest_events').insert(interestPayload);
      if (interestError) {
        console.warn('[analytics] interest event insert failed:', interestError.message);
        queueEvent(INTEREST_EVENT_QUEUE_KEY, interestPayload);
      }
    }
  } catch (err) {
    console.warn('[analytics] event failed:', err.message);
  }
}

export function trackDAEvent(eventName, da, context = {}, options = {}) {
  return trackAppEvent(eventName, context, { ...options, da });
}
